import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, Navigate, Outlet } from 'react-router-dom';
import { changeLayout, changeLayoutWizardStep } from '#redux/actions.js';
import { SETUP_MENU_ITEM_MAP } from '#constants/layout.js';
import { isUserAuthenticated, getLoggedInUser } from '../helpers/authUtils';
import UnresolvedLayout from '../layouts/Unresolved.jsx';
import VerticalLayout from '../layouts/VerticalLayout';
import SetupLayout from '../layouts/Setup';
import Suspended from '../pages/my-account/Suspended';
import { clearAndFetchUserData } from '../utils/userDataUtils.js';
import * as Sentry from '@sentry/react';
import Loader from '../components/Loader';
import { normalizeError } from '../utils/errorUtils.js';
import { updateUserData } from '../redux/auth/actions.js';

const PrivateRoute = (props) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(true);
    // Use Redux store data instead of local state
    const userData = useSelector((state) => state.Auth.user);

    const getLayout = () => {
        let layout = VerticalLayout;
        if (location.pathname.indexOf('/setup') === 0) {
            layout = SetupLayout;
        } else {
            layout = VerticalLayout;
        }
        return layout;
    };

    // Handle layout changes and body classes in useEffect to avoid setState during render
    useEffect(() => {
        if (location.pathname.indexOf('/setup') === 0) {
            dispatch(changeLayout('setup'));
            document.body.classList.remove('authentication-bg');
            if (document.body) document.body.classList.add('register-wizard');
            const setupStep = location.pathname.split('/')[2] || 'deposit-agreements';
            dispatch(changeLayoutWizardStep(SETUP_MENU_ITEM_MAP[setupStep]));
        } else {
            document.body.classList.remove('authentication-bg');
            document.body.classList.remove('register-wizard');
            dispatch(changeLayout('default-layout'));
        }
    }, [dispatch, location.pathname]);

    // Always fetch fresh user data for route protection
    useEffect(() => {
        const checkAuth = async () => {
            if (!isUserAuthenticated()) {
                setIsLoading(false);
                return;
            }

            try {
                await clearAndFetchUserData(dispatch);
            } catch (error) {
                // Log error to Sentry for monitoring
                Sentry.captureException(normalizeError(error), {
                    tags: {
                        component: 'PrivateRoute',
                        action: 'fetch_user_data',
                    },
                    extra: {
                        location: location.pathname,
                        errorMessage: error.message,
                    },
                });

                // Fallback to cached data if server is unavailable
                const cachedData = getLoggedInUser();
                if (cachedData && !userData) {
                    dispatch(updateUserData(cachedData));
                }
            } finally {
                setIsLoading(false);
            }
        };

        checkAuth();
    }, [dispatch, location.pathname]);

    if (isLoading) {
        return <Loader title="Welcome Back!" subtitle="Fetching Account Details..." variant="fullscreen" />;
    }

    if (!isUserAuthenticated()) {
        window.location.href = '/account/login';
        return null;
    }

    let Layout = getLayout();

    if (userData?.setupStatus === 'complete') {
        if (userData.status !== 'active' && userData.status !== 'reconcile') {
            return (
                <UnresolvedLayout>
                    <Suspended />
                </UnresolvedLayout>
            );
        }

        if (userData.crossRiverCustomer?.ofac === 'Failed' || userData.crossRiverCustomer?.pepScan === 'Failed') {
            return (
                <UnresolvedLayout>
                    <div className="mt-5">
                        <div className="text-center">
                            <h3>
                                Thank you for your interest!
                                <br />
                                <br />
                                Unfortunately we are unable to approve your application at this time.
                                <br />
                                <br />
                                You will receive an adverse action letter within 7 days.
                            </h3>
                        </div>
                    </div>
                </UnresolvedLayout>
            );
        }
        return (
            <Layout {...props}>
                <Outlet />
            </Layout>
        );
    } else {
        // Check for expired KYC status in setup flow
        if (userData.status === 'expired_kyc') {
            return (
                <UnresolvedLayout>
                    <div className="mt-5">
                        <div className="text-center">
                            <h3>
                                Your application has expired
                                <br />
                                <br />
                                We were unable to complete the verification process within the required time frame.
                                <br />
                                <br />
                                Please contact support to start a new application.
                            </h3>
                            <div className="mt-4">
                                <a href="mailto:support@edgeboost.com" className="btn btn-primary">
                                    Contact Support
                                </a>
                            </div>
                        </div>
                    </div>
                </UnresolvedLayout>
            );
        }

        // Allow /tutorials/setup only if user is at or past 'link-bank-account' step
        const allowedTutorialStatuses = ['link-bank-account', 'complete'];
        if (
            window.location.pathname.indexOf('/setup') === 0 ||
            (window.location.pathname === '/tutorials/setup' && allowedTutorialStatuses.includes(userData?.setupStatus))
        ) {
            return (
                <Layout {...props}>
                    <Outlet />
                </Layout>
            );
        } else {
            // Guard against null/undefined setupStatus
            if (!userData?.setupStatus) {
                // If setupStatus is missing, show loading and retry once
                console.warn('[PrivateRoute] Missing setupStatus, retrying...');
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
                return <Loader title="Loading..." variant="fullscreen" />;
            }
            return <Navigate to={`/setup/${userData.setupStatus}`} />;
        }
    }
};

export default PrivateRoute;
